import {
  Building2Icon,
  CheckIcon,
  ChevronDownIcon,
  EyeIcon,
  EyeOffIcon,
  FileTextIcon,
  HomeIcon,
  ImageIcon,
  InfoIcon,
  PenIcon,
  XIcon,
} from 'lucide-react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { getRouteApi } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Form } from '@/components/form/form';
import { ConfirmationModal } from '@/components/modals/confirmation-modal';
import { CloseButton } from '@/components/ui/close-button';
import { useModal } from '@/components/modals/new/use-modal';
import { cn } from '@/lib/utils';

import { type ApiPermission, useUpdateUserEntityPermissions } from '../api/entity-access';
import type { EntityAccess, EntityAccessPerson } from '../types/types';
import {
  ACCESS_LEVEL_BADGE_CLASS_NAMES,
  ACCESS_LEVEL_LABEL,
  ACCESS_LEVEL_TEXT_CLASS_NAMES,
} from './access-level-badge';

type AccessDetailsPanelProps = {
  selectedPerson: EntityAccessPerson;
  selectedEntity: EntityAccess;
  onClose: () => void;
};

type AccessOption = ApiPermission;

type AccessFormValues = {
  general: AccessOption;
  media: AccessOption;
  documentsInternal: AccessOption;
  documentsExternal: AccessOption;
};

const getDefaultValues = (selectedEntity: EntityAccess): AccessFormValues => {
  return {
    general: selectedEntity.permissions.general,
    media: selectedEntity.permissions.media,
    documentsInternal: selectedEntity.permissions.documentsInternal,
    documentsExternal: selectedEntity.permissions.documentsExternal,
  };
};

export const AccessDetailsPanel = ({
  selectedPerson,
  selectedEntity,
  onClose,
}: AccessDetailsPanelProps) => {
  const { t } = useTranslation();
  const EntityIcon = selectedEntity.type === 'estate' ? HomeIcon : Building2Icon;
  const userId = selectedPerson.user.id;
  const revokeModal = useModal();
  const { email, firstName, lastName } = selectedPerson.user;
  const personName = `${firstName ?? ''} ${lastName ?? ''}`.trim() || email;
  const route = getRouteApi('/_authenticated/_sidebar-layout/tenant/$tenantId/entity-access');
  const { tenantId } = route.useParams();

  const updatePermissions = useUpdateUserEntityPermissions({ tenantId, userId });

  const handleSave = (values: AccessFormValues) => {
    if (selectedEntity.type !== 'project' && selectedEntity.type !== 'estate') return;

    updatePermissions.mutate(
      {
        resource: selectedEntity.type,
        resourceId: selectedEntity.id,
        permissions: values,
      },
      {
        onSuccess: () => {
          toast.success(t('entity-access-save-success'));
        },
        onError: (error) => {
          toast.error(error.message);
        },
      }
    );
  };

  const handleRevoke = () => {
    if (selectedEntity.type !== 'project' && selectedEntity.type !== 'estate') return;

    updatePermissions.mutate(
      {
        resource: selectedEntity.type,
        resourceId: selectedEntity.id,
        permissions: {
          general: 'none',
          media: 'none',
          documentsInternal: 'none',
          documentsExternal: 'none',
        },
      },
      {
        onSuccess: () => {
          toast.success(t('entity-access-revoke-success'));
          onClose();
        },
        onError: (error) => {
          toast.error(error.message);
        },
      }
    );
  };
  const formValues = useMemo(() => getDefaultValues(selectedEntity), [selectedEntity]);
  // based on saved permissions, so unsaved changes in the form don't enable it
  const hasAccess = Object.values(formValues).some((level) => level !== 'none');

  return (
    <aside className="overflow-hidden rounded-md border border-background-darker bg-white text-primary-darker">
      <Form values={formValues} onSubmit={handleSave} className="flex h-full flex-col">
        <div className="flex items-center justify-between gap-3 border-b border-background-darker bg-background-lighter/50 px-2 py-2">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary-light/10 text-primary-light">
              <EntityIcon aria-hidden="true" className="size-5" />
            </span>
            <div className="min-w-0">
              <h2 className="truncate text-sm font-semibold leading-6 text-primary-darker">
                {selectedEntity.name}
              </h2>
              <p className="text-sm font-semibold capitalize leading-5 text-text-secondary">
                {t(selectedEntity.type)}
              </p>
            </div>
          </div>

          <CloseButton aria-label="Close access details" onClick={onClose} />
        </div>

        <section className="m-3 mb-0 rounded-sm border border-background-darker bg-white p-3 text-primary-darker">
          <div className="flex items-center gap-2 border-b border-background-darker/40 pb-3">
            <InfoIcon aria-hidden="true" className="size-4 shrink-0" />
            <h3 className="truncate text-sm font-semibold uppercase leading-6 tracking-widest">
              {t('general')}
            </h3>
          </div>
          <div className="mt-3 space-y-3">
            <AccessRow name="overall" label={t('access-overall-set')} />
            <AccessRow name="general" label={t('general-info')} />
          </div>
        </section>

        <section className="m-3 rounded-sm border border-background-darker bg-white p-3 text-primary-darker">
          <div className="flex items-center gap-2 border-b border-background-darker/40 pb-3">
            <ImageIcon aria-hidden="true" className="size-4 shrink-0" />
            <h3 className="truncate text-sm font-semibold uppercase leading-6 tracking-widest">
              {t('media')}
            </h3>
          </div>
          <div className="mt-3 space-y-3">
            <AccessRow name="media" label={t('media')} />
          </div>
        </section>

        <section className="m-3 rounded-sm border border-background-darker bg-white p-3 text-primary-darker">
          <div className="flex items-center gap-2 border-b border-background-darker/40 pb-3">
            <FileTextIcon aria-hidden="true" className="size-4 shrink-0" />
            <h3 className="truncate text-sm font-semibold uppercase leading-6 tracking-widest">
              {t('documents')}
            </h3>
          </div>
          <div className="mt-3 space-y-3">
            <AccessRow name="documentsInternal" label={t('internal-docs')} />
            <AccessRow name="documentsExternal" label={t('external-docs')} />
          </div>
        </section>
        <button
          type="submit"
          disabled={updatePermissions.isPending}
          className="m-3 h-10 rounded-sm border border-primary-light bg-primary-light/10 text-sm font-semibold text-primary-light"
        >
          {t('save-changes')}
        </button>
        <button
          type="button"
          onClick={revokeModal.open}
          disabled={!hasAccess || updatePermissions.isPending}
          className="m-3 inline-flex h-10 items-center justify-center gap-2 rounded-sm border border-danger bg-danger/10 text-sm font-semibold text-danger disabled:cursor-not-allowed disabled:opacity-50"
        >
          <XIcon aria-hidden="true" className="size-4" />
          {t('revoke-access')}
        </button>
      </Form>
      <ConfirmationModal
        modal={revokeModal}
        title={t('revoke-access')}
        description={t('revoke-access-confirm-description', {
          name: personName,
          entity: selectedEntity.name,
        })}
        confirmationText={t('revoke-access')}
        onSubmit={handleRevoke}
        destructive
      />
    </aside>
  );
};

const ACCESS_OPTION_ICON = {
  write: PenIcon,
  read: EyeIcon,
  none: EyeOffIcon,
};

const ACCESS_OPTIONS = ['write', 'read', 'none'] satisfies AccessOption[];

type AccessRowName = keyof AccessFormValues | 'overall';

const ACCESS_FORM_FIELDS: (keyof AccessFormValues)[] = [
  'general',
  'media',
  'documentsInternal',
  'documentsExternal',
];

const AccessRow = ({ name, label }: { name: AccessRowName; label: string }) => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext<AccessFormValues>();
  const value = name === 'overall' ? watch('general') : watch(name);
  const Icon = ACCESS_OPTION_ICON[value];

  const handleChange = (nextValue: AccessOption) => {
    if (name === 'overall') {
      ACCESS_FORM_FIELDS.forEach((fieldName) => {
        setValue(fieldName, nextValue, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      });
      return;
    }

    setValue(name, nextValue, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  return (
    <div className="flex items-center justify-between gap-4">
      <span className="min-w-0 text-sm font-semibold leading-5 text-text-primary">{label}</span>

      <DropdownMenuPrimitive.Root>
        <DropdownMenuPrimitive.Trigger
          className={cn(
            'flex h-9 w-28 shrink-0 items-center justify-between gap-2 rounded-sm border bg-white px-3 text-sm font-semibold outline-none transition-colors hover:border-primary-light hover:bg-primary-light/10 focus-visible:border-primary-light focus-visible:bg-primary-light/10 focus-visible:ring-2 focus-visible:ring-primary-light/30',
            ACCESS_LEVEL_BADGE_CLASS_NAMES[value]
          )}
        >
          <span className="flex items-center gap-2">
            <Icon aria-hidden="true" className="size-4" />
            {t(ACCESS_LEVEL_LABEL[value])}
          </span>
          <ChevronDownIcon aria-hidden="true" className="size-4 opacity-70" />
        </DropdownMenuPrimitive.Trigger>

        <DropdownMenuPrimitive.Portal>
          <DropdownMenuPrimitive.Content
            align="end"
            sideOffset={8}
            className="z-50 w-40 rounded-md border border-background-darker bg-white p-2 shadow-lg"
          >
            {ACCESS_OPTIONS.map((option) => {
              const OptionIcon = ACCESS_OPTION_ICON[option];
              const isSelected = option === value;

              return (
                <DropdownMenuPrimitive.Item
                  key={option}
                  onSelect={() => handleChange(option)}
                  className={cn(
                    'flex cursor-pointer items-center gap-3 rounded-sm px-3 py-2 text-md outline-none transition-colors hover:bg-primary-light hover:text-white data-[highlighted]:bg-primary-light data-[highlighted]:text-white hover:[&_svg]:text-white data-[highlighted]:[&_svg]:text-white',
                    ACCESS_LEVEL_TEXT_CLASS_NAMES[option],
                    isSelected && 'bg-transparent'
                  )}
                >
                  <span className="flex size-4 items-center justify-center">
                    {isSelected ? (
                      <CheckIcon aria-hidden="true" className="size-4 text-primary-darker" />
                    ) : null}
                  </span>
                  <OptionIcon aria-hidden="true" className="size-4" />
                  {t(ACCESS_LEVEL_LABEL[option])}
                </DropdownMenuPrimitive.Item>
              );
            })}
          </DropdownMenuPrimitive.Content>
        </DropdownMenuPrimitive.Portal>
      </DropdownMenuPrimitive.Root>
    </div>
  );
};
